/**
 * Normalize turnaround-card catalog for the console tab.
 * Accepts API 13 `tat_cards` (tat.versions + internal_days) and
 * API 9 `get_scopes` (tat.ranges, customer only) until Falcon registers tat_cards.
 */
const formatRangeLabel = (lower, upper) => {
  if (upper == null || upper === Infinity) {
    return lower <= 1 ? '1+' : `${lower}+`;
  }
  if (lower == null || lower <= 1) return `≤${upper}`;
  return `${lower}–${upper}`;
};

const versionsFromTat = tat => {
  if (Array.isArray(tat?.versions) && tat.versions.length) {
    return tat.versions.map(v => ({
      appliesFrom: v.effective_from || null,
      customer: (v.ranges || []).map(r =>
        r.customer_days == null ? null : Number(r.customer_days)
      ),
      internal: (v.ranges || []).map(r =>
        r.internal_days == null ? null : Number(r.internal_days)
      )
    }));
  }
  // get_scopes — one effective version, no internal days on either projection.
  if (Array.isArray(tat?.ranges) && tat.ranges.length) {
    return [
      {
        appliesFrom: tat.effective_from || null,
        customer: tat.ranges.map(r => (r.customer_days == null ? null : Number(r.customer_days))),
        internal: tat.ranges.map(() => null)
      }
    ];
  }
  return [];
};

const rangeSrcFromPresets = presetsRaw =>
  presetsRaw[0]?.tat?.versions?.[0]?.ranges || presetsRaw[0]?.tat?.ranges || [];

export const normalizeTatCardsPayload = data => {
  const trades = [];
  (data?.trades || []).forEach(trade => {
    const presetsRaw = [...(trade.presets || [])].sort(
      (a, b) => (a.position || 0) - (b.position || 0)
    );
    if (!presetsRaw.length) return;

    const rangeSrc = rangeSrcFromPresets(presetsRaw);
    if (!rangeSrc.length) return;

    const ranges = rangeSrc.map(r => formatRangeLabel(r.lower_bound, r.upper_bound));
    const upper = rangeSrc.map(r => (r.upper_bound == null ? Infinity : Number(r.upper_bound)));

    const cards = [];
    const seenCard = new Map();
    presetsRaw.forEach(p => {
      const tat = p.tat;
      if (!tat?.id) return;
      const id = String(tat.id);
      if (seenCard.has(id)) return;
      seenCard.set(id, cards.length);
      const versions = versionsFromTat(tat);
      if (!versions.length) return;
      cards.push({
        id,
        name: tat.name || `Card ${cards.length + 1}`,
        tier: cards.length + 1,
        of: 0,
        linkedPresetId: String(p.id),
        versions
      });
    });
    cards.forEach(c => {
      c.of = cards.length;
    });
    if (!cards.length) return;

    const presets = presetsRaw.map((p, i) => ({
      id: String(p.id),
      name: p.name || `Preset ${i + 1}`,
      desc: p.description || '',
      cardId: String(p.tat?.id || ''),
      rung: p.position != null ? p.position : i + 1,
      addsItems: [],
      addsAnswers: [],
      items: [],
      answers: {},
      tradeAnswers: {},
      requires: null
    }));

    const stdLike = ranges.length === 7;
    trades.push({
      trade_id: trade.trade_id,
      trade_name: trade.trade_name,
      cfg: {
        id: String(trade.trade_id),
        ranges,
        upper,
        cards,
        presets,
        singleCard: cards.length <= 1,
        customRanges: !stdLike,
        source: Array.isArray(presetsRaw[0]?.tat?.versions) ? 'tat_cards' : 'get_scopes'
      }
    });
  });
  return trades;
};
